import { ca, te, th } from "date-fns/locale";
import type { IPublicSimpleBonusTrigger, PlayWindow, Symbol } from "../../api";
import { IRandomInterval } from "../../Context/RandomInterval";
import { ISystemContext } from "../../Context/SystemContext";
import { Scale, LogGroup, Rect, Point, Size } from "../../MyHelpers";
import { DecodedImageResource } from "../../ResourceManager/ImageResource";
import { PixiSpriteResource } from "../../ResourceManager/PixiSpriteResource";
import { ResourceIdentifier } from "../../ResourceManager/ResourceIdentifier";
import { IWriteOnlyResourceManager, IReadOnlyResourceManager } from "../../ResourceManager/ResourceManager";
import { SpriteResource } from "../../ResourceManager/SpriteResource";
import { Settings } from "../../Settings";
import { BonusConfigurationList } from "../../SlotGameInitData";
import { LanguageChangedCallback, Translator } from "../../Translator";
import { SlotGameMessages } from "../SlotGameScreenMessages";
import { SlotSymbol } from "../SlotSymbol";
import { SymbolsCollectionResources } from "../SpineSymbols/SymbolsCollection";
import { CanvasSprite } from "../Sprite/CanvasSprite";
import { IPaytable } from "./IPayTable";
import { IPaytableParams } from "./IPayTableFactory";
import { ICombination, IGroupedSimplePaytableData } from "./PayTable8Squares";
import { WinsMap } from "./PayTable9Squares";
import { WildBulkSlide, WildBulkSlidePaytableParams } from "./slides/WildBulkSlide";
import * as PIXI from "pixi.js";
import { ResponsivePaytable } from "../../../singlegame/ResponsiveHelpers";
import { SymbolType } from "../../../singlegame/singlegame_api";

const GOLDEN_TXT = new PIXI.TextStyle({
    fontFamily: 'NotoSansDisplay-Bold',
    fontSize: Scale.fh(86),
    //fontStyle: 'italic',
    fontWeight: 'bold',
    fill: ["#F2E416", "#F2BE22", "#C2760E", "#F2BE22", "#F2E416"], // gradient
    stroke: 'black',
    strokeThickness: Scale.fh(10),
    lineJoin: 'round',
    // textBaseline: "middle",
});
const INSTRUCTIONS = new PIXI.TextStyle({
    fontFamily: 'NotoSansDisplay-Bold',
    fontSize: Scale.fh(37),
    //fontStyle: 'italic',
    fontWeight: 'bold',
    align: "center",
    fill: "white",//["#F2E416", "#F2BE22", "#C2760E", "#F2BE22", "#F2E416"], // gradient
    stroke: 'black',
    strokeThickness: Scale.fh(10),
    lineJoin: 'round',
    // wordWrapWidth: Scale.fh(300),
    // wordWrap: true,
    trim: true,
    // textBaseline: "middle",
});

interface infoElement {
    symbolType: SymbolType;
    count: number;
    win: number;
    text: PIXI.Text;
}
interface GROUP_INFO {
    name: string;
    coordinate: Point;
    location: string;
}

const PAYTABLE_POSITIONS: Record<string, GROUP_INFO> = {
    "TopLeft": {
        name: "TopLeft",
        coordinate: new Point(0, 0),
        location: "left",
    },
    "MiddleLeft": {
        name: "MiddleLeft",
        coordinate: new Point(0, 1),
        location: "left",
    },
    "BottomLeft": {
        name: "BottomLeft",
        coordinate: new Point(0, 2),
        location: "left",
    },
    "TopCenter": {
        name: "TopCenter",
        coordinate: new Point(1, 0),
        location: "center",
    },
    "MiddleCenter": {
        name: "MiddleCenter",
        coordinate: new Point(1, 1),
        location: "center",
    },
    "BottomCenter": {
        name: "BottomCenter",
        coordinate: new Point(1, 2),
        location: "center",
    },
    "TopRight": {
        name: "TopRight",
        coordinate: new Point(2, 0),
        location: "right",
    },
    "MiddleRight": {
        name: "MiddleRight",
        coordinate: new Point(2, 1),
        location: "right",
    },
    "BottomRight": {
        name: "BottomRight",
        coordinate: new Point(2, 2),
        location: "right",
    },
}

export class PayTable9SquaresPIXI implements IPaytable {

    protected paytable: IGroupedSimplePaytableData;
    protected slotSymbolsData: SlotSymbol[];
    protected view: HTMLElement = null;
    protected winsToUpdate: WinsMap[] = [];
    protected nameGame: string;
    protected basepath: string;
    protected resid: ResourceIdentifier;
    protected logGroup: LogGroup = new LogGroup("PayTable8Squares");
    protected systemContext: ISystemContext;
    protected translator: Translator = null;
    protected playWindow: PlayWindow = null;
    protected playlines: number[][] = [];
    protected scattersTrigger: string[] = [];
    readonly symbolsCollection: Map<string, SymbolsCollectionResources> = null;
    private betWinMap: Map<string, infoElement> = new Map<string, infoElement>();
    private symbolsByID: Map<string, string> = new Map<string, string>();

    public constructor(paytableParams: IPaytableParams) {

        this.symbolsCollection = paytableParams.symbolsCollection;
        this.paytable = paytableParams.paytable as IGroupedSimplePaytableData;
        this.nameGame = paytableParams?.initData?.Meta?.onlineParams?.original ?? paytableParams.nameGame;
        this.basepath = Settings.ResourcesFolder + "multigame/slot/" + this.nameGame + "/";
        this.resid = paytableParams.resid;
        this.systemContext = paytableParams.systemContext.CreateSubContext("paytable9Squares-pixi");
        this.translator = paytableParams.translator;
        this.slotSymbolsData = paytableParams.symbols;

        this.slotSymbolsData.forEach(symbol => {
            this.symbolsByID.set(symbol.ID, symbol.SymbolType);
        });
        this.playlines = paytableParams.initData.Playlines;
    }
    private win: string = null;
    private multiplier: number = 1;

    public SetBet(message: SlotGameMessages.BetChange) {

        if (!this.payElement?.WinCredits) {
            return;
        }

        const totalBet = message.betTotalCredit;
        this.multiplier = totalBet / this.paytable.PaytableForBetCredits;


        for (const [index, element] of this.betWinMap) {
            element.text.text = `${element.count}x ${this.CalculateWin(element.symbolType, element.win, totalBet)}`;
        }
    }

    private CalculateWin(symbolType: SymbolType, win: number, totalBet: number): string {
        
        if (symbolType == SymbolType.Scatter || symbolType == SymbolType.ScatterClassic) {
            return `${win * totalBet}`;
        }
        else {
            return `${win * this.multiplier}`;
        }
    }



    public Update(force: boolean = true): void {
    }
    View(): HTMLElement {
        return null
    }
    private titleSprite: PIXI.Sprite = null;

    public async PrepareResources(resourceManager: IWriteOnlyResourceManager): Promise<void> {

        resourceManager.Add(new DecodedImageResource(this.basepath + "resources/PayTable8Squares-Left.png", this.resid.ScopedResourceName(["paytable", "background-left", "pixi"])));
        resourceManager.Add(new DecodedImageResource(this.basepath + "resources/PayTable8Squares-Right.png", this.resid.ScopedResourceName(["paytable", "background-right", "pixi"])));
        resourceManager.Add(new DecodedImageResource(this.basepath + "resources/PayTable8Squares-Center.png", this.resid.ScopedResourceName(["paytable", "background-center", "pixi"])));
        // resourceManager.AddGlobal(new PixiSpriteResource(this.basepath + "resources/title_sprite2", ResourceIdentifier.Global.Add(this.nameGame).Add("titlePaytable"), true, this.logGroup.Add("titlePaytable"), true));
        resourceManager.Add(new DecodedImageResource(this.basepath + `resources/title.png`, this.resid.ScopedResourceName(["titlePaytable"])));


    }
    private SquareSize: Rect = null;
    private ImagesMap: Map<string, PIXI.Texture> = new Map<string, PIXI.Texture>();

    public async ResourcesLoaded(resourceManager: IReadOnlyResourceManager): Promise<void> {

        const leftSquare = resourceManager.Get<DecodedImageResource>(this.resid.ScopedResourceName(["paytable", "background-left", "pixi"]));
        this.ImagesMap.set("left", (PIXI.Texture.from(leftSquare.DecodedImage())));

        const centerSquare = resourceManager.Get<DecodedImageResource>(this.resid.ScopedResourceName(["paytable", "background-center", "pixi"]));
        this.ImagesMap.set("center", (PIXI.Texture.from(centerSquare.DecodedImage())));

        const rightSquare = resourceManager.Get<DecodedImageResource>(this.resid.ScopedResourceName(["paytable", "background-right", "pixi"]));
        this.ImagesMap.set("right", (PIXI.Texture.from(rightSquare.DecodedImage())));

        const title = resourceManager.Get<DecodedImageResource>(this.resid.ScopedResourceName(["titlePaytable"]));
        this.titleSprite = new PIXI.Sprite(PIXI.Texture.from(title.DecodedImage()));
        this.titleSprite.name = "titleSprite";

        // this.SquareSize = new Rect(0, 0, Scale.W(1920) / 3, Scale.H(1080) / 3);

    }
    protected created: boolean = false;
    private gridTemplate: ResponsivePaytable = null;
    public Build(): void {

        this.gridTemplate = globalThis?.gameViewAppPixiResponsive?.GetGridTemplate() ?? null;
        this.createdGroup = null;

        if (!this.gridTemplate) {
            throw new Error("gridTemplate is null");
        }

        this.SquareSize = new Rect(0, 0, Scale.W(1920) / this.gridTemplate.internalColumns, Scale.H(1080) / this.gridTemplate.internalRows);

        // const titleContainer = new PIXI.Container();
        // titleContainer.addChild(this.titleSprite);
        // this.titleSprite.position.y = Scale.H(1080) / 2 - this.titleSprite.height / 2;

        this.gridTemplate.AddTitle(this.titleSprite);

        for (let group in this.paytable.Groups) {
            this.buildPaytableGrid(this.gridTemplate, group);
        }

        this.created = true;
        this.OnResize();
    }

    //private scatterInfoPosition: Point = new Point(0, 0);
    // private wildInfoPosition: Point = new Point(0, 0);
    private wildType: string = null;

    private buildPaytableGrid(gridTemplate: ResponsivePaytable, group: string): void {

        const squareContainer = new PIXI.Container();
        squareContainer.name = `${group}-squareContainer`;

        const symbolsByGroup = this.paytable.Groups[group].Symbols;
        const positionCoordinates = PAYTABLE_POSITIONS[group].coordinate;

        const wasScatter = symbolsByGroup.find(symbol => this.symbolsByID.get(symbol) == SymbolType.Scatter || this.symbolsByID.get(symbol) == SymbolType.ScatterClassic);
        const wasWild = symbolsByGroup.find(symbolID => this.symbolsByID.get(symbolID) == SymbolType.ExpandingWildBulk || this.symbolsByID.get(symbolID) == SymbolType.ExpandingWildReel || this.symbolsByID.get(symbolID) == SymbolType.Wild);
        const createTitle = wasScatter || wasWild ? true : false;

        const squareSprite = new PIXI.Sprite(this.ImagesMap.get(PAYTABLE_POSITIONS[group].location));

        squareSprite.name = `${group}-squareSprite`;
        // squareSprite.width = this.SquareSize.width;
        // squareSprite.height = this.SquareSize.height;
        squareContainer.addChild(squareSprite);

        gridTemplate.AddChildInPositionScaled(squareContainer, positionCoordinates);

        this.SetSymbols(symbolsByGroup, gridTemplate, group);

        if (createTitle) {
            const specialSymbolTitleContainer = new PIXI.Container();

            let titleText: string = "";

            titleText = wasScatter ? "SCATTER" : "";
            titleText += wasWild ? " WILD" : "";

            specialSymbolTitleContainer.name = `${group}-${titleText.toLowerCase()}-title-Container`;

            const scatterTitle = new PIXI.Text(titleText, GOLDEN_TXT);
            scatterTitle.name = `${group}-scatterTitle`;
            // scatterTitle.position.x = this.SquareSize.width / 2;
            // scatterTitle.position.y = this.SquareSize.height / 2;
            specialSymbolTitleContainer.addChild(scatterTitle);
            gridTemplate.AddChildInPositionNoScaled(specialSymbolTitleContainer, positionCoordinates);
            specialSymbolTitleContainer.pivot.set(scatterTitle.width / 2, scatterTitle.height / 2);
            specialSymbolTitleContainer.position.x = gridTemplate.selectedColumn + this.SquareSize.width / 2;
            specialSymbolTitleContainer.position.y = gridTemplate.selectedRow + scatterTitle.height / 4;

            if (wasScatter) {
                const scatterSymbolInfoContainer = new PIXI.Container();
                scatterSymbolInfoContainer.name = `${group}-${titleText.toLowerCase()}-info-Container`;

                const scatterInfo = new PIXI.Text(this.translator.t.scatterPaysAnywhereMessage, INSTRUCTIONS);

                scatterInfo.name = `${group}-${titleText}-scatterInfo`;
                scatterSymbolInfoContainer.addChild(scatterInfo);
                gridTemplate.AddChildInPositionNoScaled(scatterSymbolInfoContainer, positionCoordinates);

                let scatterInfoPosition = new Point(gridTemplate.selectedColumn, gridTemplate.selectedRow);
                
                this.translator.SetOnLanguageChangedCallback(() => {
                    scatterInfo.text = this.translator.t.scatterPaysAnywhereMessage;
                    this.CalculateSpecialSymbolPosition(scatterSymbolInfoContainer, scatterInfoPosition);

                });
                this.CalculateSpecialSymbolPosition(scatterSymbolInfoContainer, scatterInfoPosition);
            }

            if (wasWild) {

                const wildSymbolInfoContainer = new PIXI.Container();
                wildSymbolInfoContainer.name = `${group}-${titleText.toLowerCase()}-info-Container`;

                this.wildType = this.symbolsByID.get(wasWild);

                let wildInfoText = this.wildType == SymbolType.ExpandingWildBulk ? this.translator.t.WildBulkDescriptionPixi : this.translator.t.WildReelDescription;
                const wildInfo = new PIXI.Text(wildInfoText, INSTRUCTIONS);
                wildInfo.name = `${group}-${titleText}-wildInfo`;
                wildSymbolInfoContainer.addChild(wildInfo);

                gridTemplate.AddChildInPositionNoScaled(wildSymbolInfoContainer, positionCoordinates);

                let wildInfoPosition = new Point(gridTemplate.selectedColumn, gridTemplate.selectedRow);

                this.translator.SetOnLanguageChangedCallback(() => {
                    wildInfoText = this.wildType == SymbolType.ExpandingWildBulk ? this.translator.t.WildBulkDescriptionPixi : this.translator.t.WildReelDescription;
                    wildInfo.text = wildInfoText;
                    this.CalculateSpecialSymbolPosition(wildSymbolInfoContainer, wildInfoPosition);
                });
                this.CalculateSpecialSymbolPosition(wildSymbolInfoContainer, wildInfoPosition);
            }
        }
    }

    private CalculateSpecialSymbolPosition(container: PIXI.Container, position: Point) {
        container.position.x = position.x + this.SquareSize.width / 2 - container.width / 2;
        container.position.y = position.y + this.SquareSize.height - container.height;
    }

    // private CalculateWildSymbolPosition(container: PIXI.Container, position: Point) {
    //     container.position.x = position.x + this.SquareSize.width / 2 - container.width / 2;
    //     container.position.y = position.y + this.SquareSize.height - container.height;
    // }

    private placedAtRight: boolean = false;
    private elemPayTxt: PIXI.Text;

    private SetSymbols(symbols: string[], gridContainer: ResponsivePaytable, group: string): void {

        symbols.forEach((element, index) => {

            const elemContainer = new PIXI.Container();
            const symbol = this.symbolsCollection.get("Symbol_" + element).spritesResources[index];
            console.log("symbol-info", symbol);

            let calcy = () => {
                if (symbols.length > 1) {
                    return this.SquareSize.height / 2 + (symbol.width / 2) * index;
                }
                else {
                    return this.SquareSize.height / 2;
                }
            }

            const scaleX = this.SquareSize.width / symbol.width;
            const scaleY = this.SquareSize.height / symbol.height * symbols.length;
            const scale = Math.min(scaleX, scaleY);

            elemContainer.name = `${group}-${element}-container`;

            const ele = new PIXI.Sprite(symbol);
            ele.name = `${group}-${element}`;
            ele.pivot.set(ele.width / 2, ele.height / 2);
            ele.scale.set(scale);

            const locationGroup = PAYTABLE_POSITIONS[group].location;
            this.placedAtRight = locationGroup == "right";

            ele.position.x = this.placedAtRight ? (this.SquareSize.width) * scale : this.SquareSize.width / 8 * scale;
            ele.position.y = calcy();

            elemContainer.addChild(ele)

            const payments = this?.paytable?.Groups[group]?.Pays ?? null;

            if (payments) {
                this.SetPaymentInfo(payments, gridContainer, group);
            }
            gridContainer.AddChildInPositionScaled(elemContainer, PAYTABLE_POSITIONS[group].coordinate);

        });
    }
    private payElement: ICombination = null;
    private createdGroup: string = null;

    private SetPaymentInfo(payments: ICombination[], gridTemplate: ResponsivePaytable, group: string): void {
        
        //if group is the same, do not create again
        if (this.createdGroup === group) {
            return;
        }

        console.log("payments-9quares", payments);
        const payContainer = new PIXI.Container();
        payContainer.name = `${group}-PayContainer`;

        const gap = Scale.H(20);

        const textStyle: Partial<PIXI.TextStyle> = {
            ...GOLDEN_TXT,
        }

        const maxFontSize = 85;
        const step = 8;

        payments.forEach((pay, index) => {
            
            this.payElement = this.paytable.Groups[group].Pays[index];
            const symbolId = this.paytable.Groups[group].Symbols[0];
            const symbolType = this.slotSymbolsData.find(symbol => symbol.ID == symbolId).SymbolType;
            textStyle.fontSize = Scale.fh(maxFontSize - (step * index));

            const text = `${this.payElement.Count}x ${this.CalculateWin(symbolType, this.payElement.WinCredits, 1)}`;
            this.elemPayTxt = new PIXI.Text(text, textStyle);
            this.elemPayTxt.name = `${group}-pay-${this.payElement.WinCredits}`;
            this.elemPayTxt.position.y = ((this.SquareSize.height / 4) - gap) * index;
            payContainer.addChild(this.elemPayTxt);

            const infoElement: infoElement = {
                symbolType: symbolType,
                count: this.payElement.Count,
                win: this.payElement.WinCredits,
                text: this.elemPayTxt
            };

            this.betWinMap.set(group + "-" + index, infoElement);
        });
        gridTemplate.AddChildInPositionNoScaled(payContainer, PAYTABLE_POSITIONS[group].coordinate);

        payContainer.pivot.set(payContainer.width / 2, payContainer.height / 2);
        payContainer.position.x = this.placedAtRight ? gridTemplate.selectedColumn + this.SquareSize.width / 3 : (gridTemplate.selectedColumn + (this.SquareSize.width / 3) * 2) ;
        payContainer.position.y = gridTemplate.selectedRow + this.SquareSize.height / 2;
        this.createdGroup = group;
    }

    public OnResize(): void {

        if (!this.created) {
            return;
        }
        this.gridTemplate.Resize();
    }

    StartAnim(): void {
    }
    StopAnim(): void {
    }
}